// @react
import React, {
  ReactElement,
  Fragment,
  useRef,
  useState,
  ChangeEvent,
} from 'react'
// @typings
import { ContactFormValues } from 'modules/common/forms/types/contact'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormMethods,
} from 'react-hook-form'
import clsx from 'clsx'
// @libraries / @common
import { makeStyles } from '@material-ui/core/styles'
import {
  ATD_phone_locations,
  ATD_phone_types,
} from 'modules/common/forms/fields/_config/addresss'
// @components
import Grid from '@bit/atd.web.grid'
import Divider from '@bit/atd.web.divider'
import InputSectionLabel from '@bit/atd.web.forms.input-section-label'
import InputCheckbox from '@bit/atd.web.forms.input-checkbox'
import Button from '@bit/atd.web.button'
import Select from '@bit/atd.web.select'
import InputText from '@bit/atd.web.forms.input-text'
// @module components
import RemoveModal from 'modules/common/components/RemoveModal'

const useStyles = makeStyles(() => ({
  inlineFields: {
    display: 'flex',
    '& > div:first-child': {
      width: '100px',
    },
    '& > div:last-child': {
      flexGrow: '1',
      paddingLeft: '16px',
    },
  },
  inlineFieldsHalf: {
    '& > div:first-child': {
      width: '50%',
    },
  },
}))

const MAX_LIMIT = 6

type PropsType = {
  formMethods: UseFormMethods<ContactFormValues>
  control: Control<ContactFormValues>
  errors: FieldErrors<ContactFormValues>
  onRemove: (id: number) => Promise<boolean>
}

const PhoneFieldsArray = ({
  formMethods,
  control,
  errors,
  onRemove,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalIndex, setModalIndex] = useState<number | undefined>()
  const [modalPhoneId, setModalPhoneId] = useState<number | undefined>()

  /****************************************
   * REFS
   *****************************************/

  const countryCodeRef = useRef<HTMLInputElement | null>(null)
  const numberRef = useRef<HTMLInputElement | null>(null)
  const typeRef = useRef<HTMLInputElement | null>(null)
  const locationRef = useRef<HTMLInputElement | null>(null)

  /****************************************
   * HOOKS
   *****************************************/

  const classes = useStyles()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'phones',
  })

  /****************************************
   * FUNCTIONS
   *****************************************/

  const validatePhoneTypeLocationHandler = (
    value: string,
    index: number,
    type: 'type' | 'location',
  ) => {
    const currentValues = formMethods.getValues()?.phones // all current fields
    const newPhoneValues = currentValues.map((phone, currIndex) => {
      if (index !== currIndex) return phone

      if (type === 'type') {
        return {
          ...phone,
          type: value,
        }
      }
      if (type === 'location') {
        return {
          ...phone,
          location: value,
        }
      }

      return phone
    })

    const otherPhoneValues = newPhoneValues.filter(
      (phone) => phone !== newPhoneValues[index],
    )

    const isDupePhone = otherPhoneValues.some((phone) => {
      if (
        phone.type === '' ||
        newPhoneValues[index].type === '' ||
        phone.location === '' ||
        newPhoneValues[index].location === ''
      ) {
        return false
      }
      return (
        phone.type === newPhoneValues[index].type &&
        phone.location === newPhoneValues[index].location
      )
    })

    if (isDupePhone) {
      formMethods.setError(`phones[${index}].type`, {
        type: 'check-unique-phone-type',
        message: 'Type has already been selected',
      })
      formMethods.setError(`phones[${index}].location`, {
        type: 'check-unique-phone-location',
        message: 'Location has already been selected',
      })
    } else {
      const hasErrors = errors?.phones?.some((error) => {
        return (
          error?.type?.type === 'check-unique-phone-type' ||
          error?.location?.type === 'check-unique-phone-location'
        )
      })
      const removeErrorIndex = errors?.phones?.findIndex(
        (error) =>
          error?.type?.type === 'check-unique-phone-type' ||
          error?.location?.type === 'check-unique-phone-location',
      )

      if (hasErrors) {
        formMethods.clearErrors([
          `phones[${removeErrorIndex as number}].type`,
          `phones[${removeErrorIndex as number}].location`,
        ])
      }
    }
  }

  /**
   *
   */
  const stringifyNumber = (n: number) => `Phone Number ${n - 1}`

  /**
   *
   */
  const getMask = (index: number, item: any) => {
    const phone =
      (control?.getValues()?.phones && control.getValues().phones[index]) ||
      item
    return phone.countryCode?.startsWith('1') ? '(999) 999-9999' : ''
  }

  /****************************************
   * RENDER
   *****************************************/

  return (
    <>
      {fields.map((item, index) => (
        <Fragment key={item.id}>
          {/* Divider - Shown after initial index */}
          {index > 0 && (
            <Grid xs={12} item>
              <Divider width="100%" color="#DBD9D6" height="1px" />
            </Grid>
          )}

          {/* Legend - Header */}
          <Grid xs={12} item>
            {index === 0 ? (
              <InputSectionLabel component={'legend'}>
                Primary Phone Number
              </InputSectionLabel>
            ) : (
              <InputSectionLabel component={'legend'}>
                {stringifyNumber(index + 1)}
              </InputSectionLabel>
            )}
          </Grid>

          {/* Phone ID */}
          <Controller
            control={control}
            name={`phones[${index}].phoneId`}
            defaultValue={item.phoneId}
            render={(props) => <input hidden type="text" {...props} />}
          />

          {/* IsPrimary */}
          <Controller
            control={control}
            name={`phones[${index}].isPrimary`}
            defaultValue={item.isPrimary}
            render={(props) => <input hidden type="text" {...props} />}
          />

          <Grid className={classes.inlineFields} xs={12} sm={5} item>
            {/* Phone Country Code */}
            <Controller
              control={control}
              name={`phones[${index}].countryCode`}
              onFocus={() => countryCodeRef?.current?.focus()}
              defaultValue={item.countryCode}
              render={({ onChange, onBlur, value, name }) => (
                <InputText
                  // required
                  name={name}
                  label="Country"
                  placeholder="+00"
                  mask="+9999"
                  maskplaceholder={null}
                  value={value as string}
                  inputRef={countryCodeRef}
                  onBlur={onBlur}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const formatValue = event.target.value
                      .replace('+', '')
                      .trim()
                    formMethods.clearErrors()
                    onChange(formatValue)
                  }}
                  error={!!errors?.phones?.[index]?.countryCode}
                  errorText={errors?.phones?.[index]?.countryCode?.message}
                />
              )}
            />

            {/* Phone Number */}
            <Controller
              control={control}
              name={`phones[${index}].number`}
              onFocus={() => numberRef?.current?.focus()}
              defaultValue={item.number}
              render={({ onChange, onBlur, value, name }) => (
                <InputText
                  // required
                  name={name}
                  label={`Phone Number`}
                  placeholder="(000) 000-0000"
                  mask={getMask(index, item)}
                  value={value as string}
                  inputRef={numberRef}
                  onBlur={onBlur}
                  onChange={onChange}
                  error={!!errors?.phones?.[index]?.number}
                  errorText={errors?.phones?.[index]?.number?.message}
                />
              )}
            />
          </Grid>

          {/* Phone Type / Location */}
          <Grid
            item
            className={clsx(classes.inlineFields, {
              [classes.inlineFieldsHalf]: true,
            })}
            xs={12}
            sm={7}
          >
            {/* Phone Type */}
            <Controller
              control={control}
              name={`phones[${index}].type`}
              onFocus={() => typeRef?.current?.focus()}
              defaultValue={item.type}
              render={({ onChange, onBlur, value, name }) => (
                <Select
                  // required
                  native
                  name={name}
                  label="Type"
                  placeholder={`${ATD_phone_types[0].label}`}
                  value={value as string}
                  inputRef={typeRef}
                  options={ATD_phone_types}
                  onBlur={onBlur}
                  onChange={(event, child) => {
                    validatePhoneTypeLocationHandler(
                      event.target.value as string,
                      index,
                      'type',
                    )
                    onChange(event, child)
                  }}
                  error={!!errors?.phones?.[index]?.type}
                  errorText={errors?.phones?.[index]?.type?.message}
                />
              )}
            />

            {/* Phone Location */}
            <Controller
              control={control}
              name={`phones[${index}].location`}
              onFocus={() => locationRef?.current?.focus()}
              defaultValue={item.location}
              render={({ onChange, onBlur, value, name }) => (
                <Select
                  // required
                  native
                  name={name}
                  label="Location"
                  placeholder={`${ATD_phone_locations[0].label}`}
                  value={value as string}
                  inputRef={locationRef}
                  options={ATD_phone_locations}
                  onBlur={onBlur}
                  onChange={(event, child) => {
                    validatePhoneTypeLocationHandler(
                      event.target.value as string,
                      index,
                      'location',
                    )
                    onChange(event, child)
                  }}
                  error={!!errors?.phones?.[index]?.location}
                  errorText={errors?.phones?.[index]?.location?.message}
                />
              )}
            />
          </Grid>

          {/* Do not call / SMS Text */}
          <Grid xs={12} item>
            {/* Do not call */}
            <Controller
              control={control}
              name={`phones[${index}].doNotCall`}
              defaultValue={item.doNotCall}
              render={({ onBlur, value, name }) => (
                <InputCheckbox
                  name={name}
                  label="Do not call"
                  labelPlacement="right"
                  size="small"
                  checked={value as boolean}
                  onBlur={onBlur}
                  onChange={() =>
                    formMethods.setValue(name, !value, {
                      shouldValidate: false,
                      shouldDirty: true,
                    })
                  }
                />
              )}
            />
          </Grid>

          {/* Button - Remove Phone */}
          {index > 0 && (
            <Grid xs={12} item>
              <Button
                onClick={() => {
                  setModalOpen(true)
                  setModalIndex(index)
                  setModalPhoneId(item.phoneId)
                }}
                size="medium"
                variant="secondary"
              >
                Remove Phone Number
              </Button>
            </Grid>
          )}
        </Fragment>
      ))}

      {/* Add More */}
      {fields.length < MAX_LIMIT && (
        <>
          <Grid xs={12} item>
            <Button
              onClick={() =>
                append({
                  phoneId: '',
                  isPrimary: false,
                  countryCode: '',
                  areaCode: '',
                  number: '',
                  type: '',
                  location: '',
                  doNotCall: false,
                  // smsText: false,
                })
              }
              size="medium"
            >
              Add Phone Number
            </Button>
          </Grid>
        </>
      )}
      {modalIndex !== undefined && (
        <RemoveModal
          isOpen={modalOpen}
          setIsOpen={setModalOpen}
          submitHandler={async () => {
            if (!modalPhoneId) return remove(modalIndex)
            const res = await onRemove(modalIndex)
            res && remove(modalIndex)
          }}
        />
      )}
    </>
  )
}

export default PhoneFieldsArray
